// Dedicated authorised print route. The live plan subscription supplies the
// schedule; the existing settings read supplies the server-decided export
// capability and optional contact line. No print-only query or role inference.

import { PRINT_SCHEDULE_MESSAGES as COPY } from "@herefor/product-language";
import { useQuery } from "convex/react";
import { useState } from "react";

import {
  PrintSchedule,
  toPrintScheduleSource,
  type PrintableLivePlanResult,
} from "../features/print";
import { circleSettingsQuery } from "./circleSettings";
import { practicalUpdateQueries } from "./practicalUpdates";

export function PrintRoute({ result }: { readonly result: PrintableLivePlanResult }) {
  const [generatedAt] = useState(() => Date.now());
  const settings = useQuery(circleSettingsQuery, { circleId: result.circleId });
  // SKIPPED UNTIL THE SERVER SAYS THIS MEMBER MAY EXPORT (bead hf-umv). The print
  // read is `plan.export`-gated, so subscribing to it for a member without that
  // capability throws at the boundary before the guard below is ever reached — and
  // the route boundary turns that into "this page isn't available", which is not
  // what this surface means to say. Same shape as the settings route's feed panel
  // (bead hf-e3b-circle-people-r3z.2): ask the server first, subscribe second.
  const practicalUpdates = useQuery(
    practicalUpdateQueries.forPrint,
    settings?.canUseCalendarFeed === true ? { circleId: result.circleId } : "skip",
  );

  if (settings === undefined) return <PrintRouteStatus message={COPY.loading} />;
  if (settings?.canUseCalendarFeed !== true) return <PrintRouteStatus message={COPY.unavailable} />;
  if (practicalUpdates === undefined) return <PrintRouteStatus message={COPY.loading} />;

  // `settings` decided WHETHER this member may print (plan.export); nothing on the
  // page comes from it. The supported person travels on the plan read (bead hf-86o).
  return <PrintSchedule source={toPrintScheduleSource(result, generatedAt, practicalUpdates)} />;
}

function PrintRouteStatus({ message }: { readonly message: string }) {
  return (
    <section>
      <h1>{COPY.heading}</h1>
      <p>{message}</p>
    </section>
  );
}
